import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';

import DataConnector from 'connect/DataConnector';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinStore, {SearchResult} from 'molbio/proteins/ProteinStore';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import CtrlFView from 'ui-library/CtrlFView';
import * as keyboard from 'util/Keyboard';

const PROTEIN_SEARCH_EVENT_NAMESPACE = 'protein-ctrl-f-view';

class ProteinCtrlFViewConnector extends DataConnector<ProteinCtrlFViewProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [ProteinStore, SidepanelStore, FullScreenStore];

  enhanceProps(): EnhancedProps {
    return {
      selectedIndex: this.proteinStore().getSelectedSearchResultIndex(),
      selectedSearchResult: this.proteinStore().getSelectedSearchResult(),
      numSearchResults: this.proteinStore().getNumSearchResults(),
      pendingOpenSearchView: this.proteinStore().getPendingOpenSearchView(),
      sidepanelIsExpanded: this.sidepanelStore().getIsExpanded(),
      fullScreenPageRendered: this.fullScreenStore().getCurrentScreen() != null,
    };
  }
}

export interface ProteinCtrlFViewProps {
  jumpToIndex: (index: number) => void;
}
interface EnhancedProps {
  selectedIndex: number;
  selectedSearchResult: SearchResult | undefined;
  numSearchResults: number;
  pendingOpenSearchView: boolean;
  sidepanelIsExpanded: boolean;
  fullScreenPageRendered: boolean;
}

export interface ProteinCtrlFViewState {
  query: string;
  visible: boolean;
}

export class ProteinCtrlFView extends React.Component<
  ProteinCtrlFViewProps & EnhancedProps,
  ProteinCtrlFViewState
> {
  state: ProteinCtrlFViewState = {
    query: '',
    visible: false,
  };

  searchRef = React.createRef<CtrlFView>();

  componentDidMount(): void {
    $(window).on(`keydown.${PROTEIN_SEARCH_EVENT_NAMESPACE}`, (e) => {
      if (keyboard.isCtrlF(e)) {
        if (this.props.sidepanelIsExpanded || this.props.fullScreenPageRendered) {
          return;
        }

        e.preventDefault();
        this._handleOpenSearchView();
      }
    });
  }

  componentDidUpdate(
    prevProps: ProteinCtrlFViewProps & EnhancedProps,
    prevState: ProteinCtrlFViewState
  ): void {
    if (!prevState.visible && this.state.visible) {
      this._handleQueryChange(this.state.query);
    }

    const selectedSearchResultChanged = !isEqual(
      prevProps.selectedSearchResult,
      this.props.selectedSearchResult
    );
    if (this.props.selectedSearchResult != null && selectedSearchResultChanged) {
      this.props.jumpToIndex(this.props.selectedSearchResult.index);
    }
    if (this.props.pendingOpenSearchView) {
      this._handleOpenSearchView();
      ProteinActions.completeOpenSearchView();
    }
  }

  componentWillUnmount(): void {
    $(window).off(`keydown.${PROTEIN_SEARCH_EVENT_NAMESPACE}`);
  }

  _handleOpenSearchView = (): void => {
    if (this.state.visible) {
      this.searchRef.current!.focusAndSelect();
    } else {
      this.show();
    }
  };

  _handleQueryChange = (query: string): void => {
    if (query.length === 0) {
      ProteinActions.clearSearchResults();
    } else {
      ProteinActions.searchAminoAcids(query);
    }
    this.setState({query});
  };

  _handleViewNextResult = (): void => {
    ProteinActions.viewNextSearchResult();
  };

  _handleViewPreviousResult = (): void => {
    ProteinActions.viewPreviousSearchResult();
  };

  hide = (): void => {
    ProteinActions.clearSearchResults();
    this.setState({visible: false});
  };

  _handleClose = (): void => {
    this.hide();
  };

  show = (): void => {
    this.setState({visible: true});
  };

  render(): JSX.Element | null {
    if (!this.state.visible || this.props.sidepanelIsExpanded) {
      return null;
    }
    return (
      <CtrlFView
        ref={this.searchRef}
        onClose={this._handleClose}
        onQueryChange={this._handleQueryChange}
        query={this.state.query}
        onViewNextResult={this._handleViewNextResult}
        onViewPreviousResult={this._handleViewPreviousResult}
        selectedIndex={this.props.selectedIndex}
        numSearchResults={this.props.numSearchResults}
      />
    );
  }
}

export default ProteinCtrlFViewConnector.connect(ProteinCtrlFView);
